import React from 'react'

import { CommonCardProps } from 'component-library/cards/CardItem.Props'
import { ImageWidth, ImageWidths } from 'common/helpers/image'

import { Props as PictureProps, Picture } from 'component-library/content/Picture/Picture'

import {
    StyledMediaContent,
    StyledMedia,
    StyledCardPlayButton,
    StyledGalleryButton,
    StyledGalleryIcon,
    StyledProviderLogo,
    StyledArticleVideo,
    StyledMediaContainer,
} from 'component-library/cards/CardMedia/StyledMedia.styled'

import { FixedRatio, ImageSource, CardBreakpointRatios } from 'common/client-data-types/image-item'
import { colors } from 'component-library/__styling/settings/colors'
import { Theme } from 'component-library/__styling/themes'
import { Product } from 'component-library/__product'
import { themedValue } from 'component-library/__styling/themed-value'
import { PlayButton } from 'component-library/buttons/PlayButton/PlayButton'
import {
    ImageSizeTypes,
    getImageSizePropsFromType,
} from 'component-library/content/Picture/layouts/helpers/sizes'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { GetVideoQueue, VideoOptions } from 'component-library/content/Video/PlayerInterface'
import { VideoDetails } from 'common/client-data-types/VideoDetails'
import { Props as ArticleVideoProps } from 'component-library/publication/ArticleVideo/ArticleVideo'
import inViewport, { ViewportProps } from 'component-library/__helpers/in-viewport'
import { useFeature } from 'common/feature-togglings/features'
import {
    getCompatibleBreakpointRatios,
    fixedRatiosToCardBreakpoints,
    doesParticularRatioExist,
} from './image-helpers'
import { useTheme } from '@emotion/react'
import {
    PublicationCardItem,
    isPublicationCardItemWithVideo,
} from 'common/client-data-types/card-types'
import { useConsentGiven } from 'common/hooks/useConsentGiven'
import { ConsentLevel } from 'common/data/consent/consent.redux'

export type MediaMode = 'hidden' | 'edgeToEdge' | 'fullHeight' | 'heroVideoInline' | 'default'

export interface Props extends CommonCardProps {
    hasBackground?: boolean
    hasGradientOverlay?: boolean
    /**
     * defaults to `4:3`
     */
    fixedRatio?: FixedRatio | FixedRatio[] | CardBreakpointRatios

    imageWidths?: ImageWidths['thewest'] | ImageWidths['perthnow'] | ImageWidths['sevennews']

    imageLayout?: PictureProps['layout']
    mediaMode?: MediaMode
    hideMediaIcon?: boolean
    providerLogo?: string
    className?: string
    // setInnerRef is not named this way so that the component can be styled() and not get an emotion innerRef warning
    setInnerRef?: (ref: any) => void
    /**
     * flag to modify card styles if a video will play inline
     */
    willPlayVideoInline?: boolean
    getVideoQueue?: GetVideoQueue
    adUnitPath?: string

    isLarge?: boolean
    item: MaybeLoaded<PublicationCardItem>
}

export type PropsInternal = Props & ViewportProps

export const InternalCardMedia: React.FC<PropsInternal> = props => {
    const theme = useTheme()
    // this is an inline video
    const enableNielsenVideoReporting = useFeature('nielsen-video-events-inline-videos')
    const enableSnowplow = useFeature('snowplow')
    const enablePrebid = useFeature('prebid-video')
    const canLoadVideoPlayer = useConsentGiven({
        consentRequiredLevel: ConsentLevel.Advertising | ConsentLevel.Analytics,
    })

    // Both innerRef AND setInnerRef are used here - this is due to emotion warning for usages of "innerRef". We
    // still have to consume innerRef however because this is what is the inViewport HOC requires
    const setMediaRef = (ref: any) => {
        props.innerRef(ref)
        if (props.setInnerRef) {
            props.setInnerRef(ref)
        }
    }

    const getImageElement = (
        image: ImageSource = theme.fallbackImages,
        fixedRatios: FixedRatio[] | CardBreakpointRatios,
        imageWidths: ImageWidth,
        className: string | undefined,
        iconOverlay: false | JSX.Element = false,
        providerIcon: false | JSX.Element = false
    ) => {
        const breakpointRatio: CardBreakpointRatios = Array.isArray(fixedRatios)
            ? { default: fixedRatios }
            : fixedRatios

        const imageWithFallbacks: ImageSource = {
            ...image,
            crops: {
                original: image.crops.original,
                '161:229': image.crops['161:229'] || theme.fallbackImages.crops['161:229'],
                '16:9': image.crops['16:9'] || theme.fallbackImages.crops['16:9'],
                '316:421': image.crops['316:421'] || theme.fallbackImages.crops['316:421'],
                '4:3': image.crops['4:3'] || theme.fallbackImages.crops['4:3'],
            },
        }

        const fixedRatio = getCompatibleBreakpointRatios(image.crops, breakpointRatio)

        // Set all image as Edge to Edge for the west
        const themeMediaMode: MediaMode = themedValue(theme, {
            thewest: 'edgeToEdge',
            sevennews: 'default',
            perthnow: 'default',
            fallback: 'default',
        })

        return (
            <StyledMedia
                className={className}
                ref={setMediaRef}
                hasGradientOverlay={props.hasGradientOverlay}
                mediaMode={props.mediaMode || themeMediaMode}
            >
                <StyledMediaContent>
                    {iconOverlay}
                    {providerIcon}
                    <Picture
                        image={imageWithFallbacks}
                        fixedRatio={fixedRatio}
                        // Need to use any here because Picture props are a tagged union based
                        // on this property
                        layout={(props.imageLayout as any) || 'fill-container-horizontally'}
                        imageWidths={imageWidths}
                        disableLazyLoad={props.disableImageLazyLoad}
                        imageSizes={getImageSizesFromWidth(theme, props.isLarge, imageWidths)}
                    />
                </StyledMediaContent>
            </StyledMedia>
        )
    }

    const getPlayButton = () => {
        const showPlayButton =
            !props.hideMediaIcon &&
            (theme.kind !== Product.TheWest || props.willPlayVideoInline === true)

        const renderPlayButton = () => {
            if (theme.kind === 'sevennews') {
                return <PlayButton isIcon isSmall={props.isLarge !== true} isCentered />
            }
            return <StyledCardPlayButton />
        }

        return showPlayButton && renderPlayButton()
    }

    const imageWidths =
        props.imageWidths !== undefined
            ? props.imageWidths
            : getImageWidth(theme.kind, props.isLarge)

    const { fixedRatio = '4:3' as FixedRatio, className } = props

    const publicationKind = props.item.loaded ? props.item.result.publicationKind : 'unknown'

    const backgroundColor = themedValue(theme, {
        perthnow: props.hasBackground ? colors.perthnow.greyBasalt : undefined,
        fallback: undefined,
    })

    if (!props.item.loaded) {
        return (
            <StyledMedia
                className={className}
                ref={setMediaRef}
                hasGradientOverlay={props.hasGradientOverlay}
                mediaMode={props.mediaMode || 'default'}
            >
                <Picture
                    layout="placeholder"
                    fixedRatio={fixedRatiosToCardBreakpoints(fixedRatio, '4:3')}
                    loadingBackgroundColor={backgroundColor}
                />
            </StyledMedia>
        )
    }

    const cardItem = props.item.result

    const breakpointRatio: CardBreakpointRatios = Array.isArray(fixedRatio)
        ? { default: fixedRatio }
        : typeof fixedRatio === 'string'
        ? { default: fixedRatio }
        : fixedRatio

    if (theme.kind === 'perthnow' && publicationKind === 'gallery') {
        const styledGalleryButton = (
            <StyledGalleryButton>
                <StyledGalleryIcon />
            </StyledGalleryButton>
        )

        return getImageElement(
            cardItem.image,
            breakpointRatio,
            imageWidths,
            className,
            styledGalleryButton
        )
    }

    if (
        isInlineVideoCompatible(props) &&
        isPublicationCardItemWithVideo(cardItem) &&
        canLoadVideoPlayer
    ) {
        if (process.env.NODE_ENV === 'test' || props.inViewport > 20) {
            const { id, heading, accountId, source, videoType } = cardItem.video

            const videoDetails: VideoDetails = {
                id,
                accountId,
                heading,
                posterImage: cardItem.image,
                source,
                videoType,
            }

            const videoOptions: VideoOptions = {
                adRequestMode: 'onload' as 'onload',
                adUnitPath: props.adUnitPath || '',
                preloadOption: 'none',
                canAutoplayVideos: false,
                compactMode: true,
                enableNielsenVideoReporting,
                enableSnowplow,
                enablePrebid,
            }

            const videoProps: ArticleVideoProps = {
                showTitles: true,
                videoDetails,
                autoplayOptions: false,
                autoplayNextOptions: {
                    delayDuration: 5,
                },
                videoOptions,
                isHeroVideo: true,
                pauseOverlay: true,
                getVideoQueue: props.getVideoQueue!,
                isVideoStickyEnabled: false,
                onEvent: props.onEvent,
            }
            return (
                <StyledMediaContainer mediaMode={props.mediaMode || 'default'}>
                    <StyledArticleVideo {...videoProps} />
                </StyledMediaContainer>
            )
        } else {
            return getImageElement(
                cardItem.image,
                breakpointRatio,
                imageWidths,
                className,
                getPlayButton(),
                !!cardItem.providerLogo && <StyledProviderLogo src={cardItem.providerLogo} alt="" />
            )
        }
    } else if (isPublicationCardItemWithVideo(cardItem) && canLoadVideoPlayer) {
        return getImageElement(
            cardItem.image,
            breakpointRatio,
            imageWidths,
            className,
            getPlayButton(),
            !!cardItem.providerLogo && <StyledProviderLogo src={cardItem.providerLogo} alt="" />
        )
    }

    return getImageElement(cardItem.image, breakpointRatio, imageWidths, className)
}

InternalCardMedia.displayName = 'InternalCardMedia'

export const CardMedia = inViewport<Props>(InternalCardMedia)
CardMedia.displayName = 'CardMedia'

function isInlineVideoCompatible(props: PropsInternal) {
    const { willPlayVideoInline, fixedRatio, getVideoQueue } = props
    if (!willPlayVideoInline) {
        return false
    }
    if (!fixedRatio || !doesParticularRatioExist(fixedRatio, '16:9')) {
        return false
    }
    if (!getVideoQueue) {
        return false
    }

    return true
}

function getImageWidth(product: Product, isLarge: boolean | undefined) {
    switch (product) {
        case Product.PerthNow:
            return isLarge ? 668 : 320
        case Product.TheWest:
            return isLarge ? 500 : 268
        case Product.SevenNews:
            return isLarge ? 650 : 126
    }
}

/** Determine the sizing for cards based on the product / image width */
function getImageSizesFromWidth(
    theme: Theme,
    isLarge: boolean | undefined,
    imageWidth: ImageWidth
): string | undefined {
    switch (theme.kind) {
        case Product.SevenNews:
        case Product.PerthNow:
            if (imageWidth === 320) {
                return getImageSizePropsFromType(theme.kind, ImageSizeTypes.ListingCard, imageWidth)
            }
            break

        case Product.TheWest:
            if (imageWidth === 268) {
                return getImageSizePropsFromType(theme.kind, ImageSizeTypes.ListingCard, imageWidth)
            }
            break

        default:
            if (!isLarge && imageWidth >= 500) {
                return getImageSizePropsFromType(theme.kind, ImageSizeTypes.LargeCard, imageWidth)
            }
            return getImageSizePropsFromType(
                theme.kind,
                isLarge ? ImageSizeTypes.LargeCard : ImageSizeTypes.Card,
                imageWidth
            )
    }

    return undefined
}
